var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import classNames from "classnames";
import { useAtom } from "jotai";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { MusicVolumeAtom, SfxVolumeAtom } from "@/atoms";
import { MusicPlayer, useAudioPlayerState } from "@/audio/MusicPlayer";
import { Tracklist } from "@/meta/Tracklist";
import styles from "./ArtbookControls.module.scss";
import { IconButton, IconTheme } from "@/components/IconButton/IconButton";
import IconDown from "@/public_assets/icon_down.svg";
import IconPlayLarge from "@/public_assets/icon_play_large.svg";
import { IconMenu } from "@/components/IconButton/IconMenu";
import { Range } from "@/components/atoms/Range";
import { EffectiveMusicVolumeAtom } from "../../atoms";
import { useAtomValue } from "jotai";
import { metrics } from "../../analytics/events";
import { useOnChange } from "../../utils/useOnChange";
export function SoundtrackControls() {
    var _a, _b;
    var _c = __read(useAtom(MusicVolumeAtom), 2), musicVolume = _c[0], setMusicVolumeState = _c[1];
    var effectiveMusicVolume = useAtomValue(EffectiveMusicVolumeAtom);
    useOnChange(function () {
        if (musicVolume.muted) {
            metrics.onMusicMuted();
        }
        else {
            metrics.onMusicUnmuted();
        }
    }, [musicVolume.muted]);
    var player = useMemo(function () {
        return new MusicPlayer();
    }, []);
    useEffect(function () {
        return function () {
            player.dispose();
        };
    }, [player]);
    useEffect(function () {
        player.setVolume(effectiveMusicVolume);
    }, [player, effectiveMusicVolume]);
    var state = useAudioPlayerState(player);
    var currentTrackIndex = Tracklist.findIndex(function (e) { return e.audio === state.track; });
    var setCurrentTrack = useCallback(function (index) {
        if (index >= 0 && index < Tracklist.length) {
            player.load(Tracklist[index].audio);
            if (!state.playing) {
                player.play();
            }
        }
    }, [player, state]);
    var prev = useCallback(function () {
        if (state.time < 3 && currentTrackIndex > 0) {
            setCurrentTrack(currentTrackIndex - 1);
            return "previous";
        }
        else {
            player.seek(0);
            return "rewind";
        }
    }, [currentTrackIndex, player, state.time, setCurrentTrack]);
    var next = useCallback(function () {
        setCurrentTrack(currentTrackIndex + 1);
    }, [currentTrackIndex, setCurrentTrack]);
    useEffect(function () {
        if (state.track === "") {
            player.load(Tracklist[0].audio);
            return;
        }
        if (state.time >= state.duration &&
            currentTrackIndex < Tracklist.length - 1) {
            next();
            player.play();
            metrics.onAutomaticNextSong(currentTrackIndex + 1);
        }
    }, [currentTrackIndex, next, player, state]);
    var _d = __read(useState(false), 2), songControlHovered = _d[0], setSongControlHovered = _d[1];
    useEffect(function () {
        if (!songControlHovered)
            return;
        var timeoutOpen = setTimeout(function () {
            metrics.clickOnOpenSongList();
        }, 300);
        var timeoutOpened = setTimeout(function () {
            metrics.songListDisplayed();
        }, 800);
        return function () {
            clearTimeout(timeoutOpen);
            clearTimeout(timeoutOpened);
        };
    }, [songControlHovered]);
    // Keyboards shortcuts
    useEffect(function () {
        function onKeyPress(e) {
            if (e.code === "Space") {
                var playing = player.getState().playing;
                if (!playing) {
                    player.play();
                }
                else {
                    player.pause();
                }
            }
        }
        document.addEventListener("keydown", onKeyPress);
        return function () {
            document.removeEventListener("keydown", onKeyPress);
        };
    }, [player]);
    return (React.createElement("div", { className: classNames(styles.controlPanel, styles.groupSoundtrack) },
        React.createElement("div", { className: styles.buttonGroup },
            React.createElement("div", { className: styles.songControl, onMouseEnter: function () { return setSongControlHovered(true); }, onMouseLeave: function () { return setSongControlHovered(false); } },
                React.createElement("div", { className: styles.trackNumber },
                    React.createElement("span", { className: styles.current }, lpad("0", 2, currentTrackIndex + 1)),
                    "/",
                    Tracklist.length),
                React.createElement("div", { className: styles.trackTitle }, (_a = Tracklist[currentTrackIndex]) === null || _a === void 0 ? void 0 : _a.title),
                React.createElement("div", { className: styles.trackTime },
                    React.createElement("span", { className: styles.current }, formatTime(state.time)),
                    "/",
                    formatTime(isNaN(state.duration)
                        ? ((_b = Tracklist[currentTrackIndex]) === null || _b === void 0 ? void 0 : _b.duration) || 0
                        : state.duration)),
                React.createElement(IconDown, { className: styles.dropdownIcon }),
                React.createElement("div", { className: styles.trackSelector },
                    React.createElement("div", { className: styles.trackList }, Tracklist.map(function (track, index) {
                        var _a;
                        return (React.createElement("div", { key: index, className: classNames(styles.trackButton, (_a = {},
                                _a[styles.current] = index === currentTrackIndex,
                                _a)), onClick: function () {
                                metrics.clickOnSong(index, state.playing);
                                setCurrentTrack(index);
                            } },
                            index === currentTrackIndex && (React.createElement(IconPlayLarge, { className: styles.trackPlayingIcon })),
                            React.createElement("span", { className: styles.trackNumber }, lpad("0", 2, index + 1)),
                            React.createElement("span", { className: styles.trackTitle }, track.title),
                            React.createElement("span", { className: styles.filler, "aria-hidden": true }, __spreadArray([], __read(new Array(64)), false).map(function () { return "."; })),
                            React.createElement("span", { className: styles.trackDuration }, formatTime(track.duration))));
                    })))),
            React.createElement(IconButton, { icon: require("@/public_assets/button_arrow_left.png"), iconHover: require("@/public_assets/button_arrow_left_hover.png"), theme: IconTheme.Dark, onClick: function () {
                    var res = prev();
                    if (res === "previous") {
                        metrics.clickOnPreviousSong(currentTrackIndex - 1, state.playing);
                    }
                }, tooltip: {
                    label: (React.createElement(FormattedMessage, { defaultMessage: "previous track", id: "WvsYPI" })),
                } }),
            state.playing ? (React.createElement(IconButton, { icon: require("@/public_assets/button_pause.png"), iconHover: require("@/public_assets/button_pause_hover.png"), theme: IconTheme.Dark, onClick: function () {
                    metrics.clickOnPause(currentTrackIndex);
                    player.pause();
                }, tooltip: {
                    label: React.createElement(FormattedMessage, { defaultMessage: "pause", id: "rDF6w5" }),
                } })) : (React.createElement(IconButton, { icon: require("@/public_assets/button_play.png"), iconHover: require("@/public_assets/button_play_hover.png"), theme: IconTheme.Dark, onClick: function () {
                    metrics.clickOnPlay(currentTrackIndex);
                    player.play();
                }, tooltip: {
                    label: React.createElement(FormattedMessage, { defaultMessage: "play", id: "4Ok82D" }),
                } })),
            React.createElement(IconButton, { icon: require("@/public_assets/button_arrow_right.png"), iconHover: require("@/public_assets/button_arrow_right_hover.png"), theme: IconTheme.Dark, onClick: function () {
                    if (currentTrackIndex < Tracklist.length - 1) {
                        metrics.clickOnNextSong(currentTrackIndex + 1, state.playing);
                    }
                    next();
                }, tooltip: {
                    label: React.createElement(FormattedMessage, { defaultMessage: "next track", id: "t3XQIq" }),
                } })),
        React.createElement("div", { className: styles.buttonGroup },
            React.createElement(IconMenu, { icon: musicVolume.muted
                    ? require("@/public_assets/button_volume_music_muted.png")
                    : require("@/public_assets/button_volume_music.png"), iconHover: musicVolume.muted
                    ? require("@/public_assets/button_volume_music_muted_hover.png")
                    : require("@/public_assets/button_volume_music_hover.png"), theme: IconTheme.Dark, arrow: true, trigger: "hover", iconSize: "large", onClick: function () {
                    return setMusicVolumeState({
                        muted: !musicVolume.muted,
                    });
                }, onToggle: function (visible) {
                    if (visible) {
                        metrics.clickOnMusicAudioSettings();
                    }
                }, menu: {
                    content: (React.createElement(Range, { value: musicVolume.muted ? 0 : musicVolume.volume, max: 1, step: 0.01, onChange: function (v) { return setMusicVolumeState({ volume: v }); } })),
                    position: "bottom",
                    theme: "dark",
                }, tooltip: {
                    label: (React.createElement(FormattedMessage, { defaultMessage: "music volume", id: "C7TbXf" })),
                } }),
            React.createElement(SfxControl, { tooltipPosition: "bottom" }))));
}
export function SfxControl(props) {
    var _a = __read(useAtom(SfxVolumeAtom), 2), sfxVolume = _a[0], setSfxVolumeState = _a[1];
    useOnChange(function () {
        if (sfxVolume.muted) {
            metrics.onSFXMuted();
        }
        else {
            metrics.onSFXUnmuted();
        }
    }, [sfxVolume.muted]);
    return (React.createElement(IconMenu, { icon: sfxVolume.muted
            ? require("@/public_assets/button_volume_sfx_muted.png")
            : require("@/public_assets/button_volume_sfx.png"), iconHover: sfxVolume.muted
            ? require("@/public_assets/button_volume_sfx_muted_hover.png")
            : require("@/public_assets/button_volume_sfx_hover.png"), theme: IconTheme.Dark, arrow: true, trigger: "hover", iconSize: "large", onClick: function () { return setSfxVolumeState({ muted: !sfxVolume.muted }); }, onToggle: function (visible) {
            if (visible) {
                metrics.clickOnSFXAudioSettings();
            }
        }, menu: {
            content: (React.createElement(Range, { value: sfxVolume.muted ? 0 : sfxVolume.volume, max: 1, step: 0.01, onChange: function (v) { return setSfxVolumeState({ volume: v }); } })),
            position: props.tooltipPosition,
            theme: "dark",
        }, tooltip: {
            label: React.createElement(FormattedMessage, { defaultMessage: "SFX volume", id: "ZJuFp/" }),
        } }));
}
function formatTime(time) {
    var mins = Math.floor(time / 60);
    var secs = Math.floor(time % 60);
    return "".concat(mins, ":").concat(lpad("0", 2, secs));
}
function lpad(char, len, s) {
    var res = s.toString();
    while (res.length < len) {
        res = char + res;
    }
    return res;
}
